/**
 * Réordonnancement optimiste du cache React Query lors d'un move_task
 * (drag liste et kanban). Reproduit côté client ce que la RPC fera côté
 * serveur, pour que la ligne se pose immédiatement au bon endroit — le
 * refetch de confirmation arrive ensuite sans saut visuel. Pur, testé.
 */

export interface MoveParams {
  taskId: string;
  targetSectionId: string | null;
  beforeTaskId?: string | null;
}

export function applyMoveInCache<
  T extends { id: string; section_id: string | null; task_order: number },
>(tasks: T[], move: MoveParams): T[] {
  const task = tasks.find((t) => t.id === move.taskId);
  if (!task) return tasks;

  const sourceSectionId = task.section_id;
  const moved = { ...task, section_id: move.targetSectionId };
  const rest = tasks.filter((t) => t.id !== move.taskId);

  let insertAt: number;
  const beforeIdx = move.beforeTaskId
    ? rest.findIndex((t) => t.id === move.beforeTaskId)
    : -1;
  if (beforeIdx >= 0) {
    insertAt = beforeIdx;
  } else {
    // Fin de la section cible : après sa dernière tâche (blocs contigus
    // dans l'ordre serveur), sinon fin de tableau si la section est vide.
    let lastOfSection = -1;
    rest.forEach((t, i) => {
      if (t.section_id === move.targetSectionId) lastOfSection = i;
    });
    insertAt = lastOfSection >= 0 ? lastOfSection + 1 : rest.length;
  }

  const next = [...rest.slice(0, insertAt), moved, ...rest.slice(insertAt)];

  // Renumérote les sections touchées pour garder task_order cohérent avec
  // l'ordre du tableau (les gardes du board s'appuient dessus).
  const touched = new Set([sourceSectionId, move.targetSectionId]);
  const counters = new Map<string | null, number>();
  return next.map((t) => {
    if (!touched.has(t.section_id)) return t;
    const n = (counters.get(t.section_id) ?? 0) + 1;
    counters.set(t.section_id, n);
    return t.task_order === n ? t : { ...t, task_order: n };
  });
}
